// planScheduleCascade.js
//
// Pure FS/SS push-only schedule cascade (day granularity).

import {
  LINK_KIND_FS,
  LINK_KIND_SS,
  isTerminalTaskStatus,
  normalizePredecessorLinks,
} from './predecessorLinks';

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_MAX_WRITES = 200;

const toDayStart = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return toDayStart(next);
};

/**
 * @param {{ start?: Date|string, end?: Date|string, startDate?: *, endDate?: * }|null} schedule
 * @returns {{ start: Date, end: Date }|null}
 */
export function normalizeDaySchedule(schedule) {
  if (!schedule || typeof schedule !== 'object') return null;
  const start = toDayStart(schedule.start ?? schedule.startDate);
  if (!start) return null;
  const endRaw = toDayStart(schedule.end ?? schedule.endDate);
  const end = endRaw && endRaw.getTime() >= start.getTime() ? endRaw : start;
  return { start, end };
}

/**
 * Inclusive day count.
 * @param {{ start: Date, end: Date }} schedule
 * @returns {number}
 */
export function scheduleDurationDays(schedule) {
  const normalized = normalizeDaySchedule(schedule);
  if (!normalized) return 1;
  const diff = Math.round((normalized.end.getTime() - normalized.start.getTime()) / DAY_MS);
  return Math.max(1, diff + 1);
}

/**
 * @param {{ start: Date, end: Date }} schedule
 * @param {Date} nextStart
 * @returns {{ start: Date, end: Date }}
 */
export function shiftSchedulePreservingDuration(schedule, nextStart) {
  const duration = scheduleDurationDays(schedule);
  const start = toDayStart(nextStart);
  const end = addDays(start, duration - 1);
  return { start, end };
}

/**
 * @param {'FS'|'SS'} kind
 * @param {{ start: Date, end: Date }} predecessorSchedule
 * @param {number} lagDays
 * @returns {Date|null}
 */
export function earliestStartFromLink(kind, predecessorSchedule, lagDays = 0) {
  const schedule = normalizeDaySchedule(predecessorSchedule);
  if (!schedule) return null;
  const lag = Math.max(0, Math.floor(Number(lagDays) || 0));
  if (kind === LINK_KIND_SS) {
    return addDays(schedule.start, lag);
  }
  // FS — same-day handoff when lag is 0
  return addDays(schedule.end, lag);
}

/**
 * Observed spacing between a predecessor and successor before an edit.
 * FS: days from pred end → succ start (0 = same day).
 * SS: days from pred start → succ start.
 *
 * @param {'FS'|'SS'} kind
 * @param {{ start?: *, end?: * }|null} predecessorSchedule
 * @param {{ start?: *, end?: * }|null} successorSchedule
 * @returns {number}
 */
export function measureLinkLagDays(kind, predecessorSchedule, successorSchedule) {
  const pred = normalizeDaySchedule(predecessorSchedule);
  const succ = normalizeDaySchedule(successorSchedule);
  if (!pred || !succ) return 0;
  const anchor = kind === LINK_KIND_SS ? pred.start : pred.end;
  const diff = Math.round((succ.start.getTime() - anchor.getTime()) / DAY_MS);
  return Math.max(0, diff);
}

/**
 * Effective lag for cascade: explicit lagDays floor, else keep the gap
 * that already existed between these two tasks.
 *
 * @param {{ kind: string, lagDays?: number }} link
 * @param {{ start?: *, end?: * }|null} baselinePredecessor
 * @param {{ start?: *, end?: * }|null} baselineSuccessor
 * @returns {number}
 */
export function resolveCascadeLagDays(link, baselinePredecessor, baselineSuccessor) {
  const stored = Math.max(0, Math.floor(Number(link?.lagDays) || 0));
  const measured = measureLinkLagDays(link?.kind, baselinePredecessor, baselineSuccessor);
  return Math.max(stored, measured);
}

/**
 * Build successor → predecessors adjacency plus predecessor → successors index.
 *
 * @param {Record<string, { predecessorLinks?: unknown }>} tasksByKey
 */
function buildGraph(tasksByKey) {
  const linksBySuccessor = {};
  const successorsByPredecessor = {};

  Object.entries(tasksByKey || {}).forEach(([taskKey, task]) => {
    const key = String(taskKey || '').trim();
    if (!key) return;
    const links = normalizePredecessorLinks(
      task?.predecessorLinks ?? task?.links ?? [],
    );
    linksBySuccessor[key] = links;
    links.forEach((link) => {
      const pred = link.taskKey;
      if (!successorsByPredecessor[pred]) successorsByPredecessor[pred] = [];
      successorsByPredecessor[pred].push(key);
    });
  });

  return { linksBySuccessor, successorsByPredecessor };
}

/**
 * BFS order of successors reachable from the changed task.
 *
 * @param {string} rootKey
 * @param {Record<string, string[]>} successorsByPredecessor
 * @returns {string[]}
 */
function orderSuccessors(rootKey, successorsByPredecessor) {
  const ordered = [];
  const seen = new Set();
  const queue = [...(successorsByPredecessor[rootKey] || [])];

  while (queue.length) {
    const key = queue.shift();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    ordered.push(key);
    (successorsByPredecessor[key] || []).forEach((succ) => {
      queue.push(succ);
    });
  }

  return ordered;
}

/**
 * Plan cascade writes after a task schedule change.
 *
 * @param {object} params
 * @param {string} params.changedTaskKey
 * @param {{ start: Date, end: Date }} params.nextSchedule
 * @param {Record<string, {
 *   schedule?: object,
 *   predecessorLinks?: unknown,
 *   status?: unknown,
 *   writable?: boolean,
 * }>} params.tasksByKey
 * @param {'push'|'pin-successors'} [params.mode]
 * @param {number} [params.maxWrites]
 * @returns {{
 *   writes: Array<{ taskKey: string, schedule: { start: Date, end: Date } }>,
 *   skipped: Array<{ taskKey: string, reason: string }>,
 *   affectedCount: number,
 * }}
 */
export function planScheduleCascade({
  changedTaskKey,
  nextSchedule,
  tasksByKey = {},
  mode = 'push',
  maxWrites = DEFAULT_MAX_WRITES,
} = {}) {
  const rootKey = String(changedTaskKey || '').trim();
  const rootSchedule = normalizeDaySchedule(nextSchedule);
  const writes = [];
  const skipped = [];

  if (!rootKey || !rootSchedule) {
    return { writes, skipped, affectedCount: 0 };
  }

  // Baseline = schedules before this edit (used to preserve existing gaps).
  const baselineSchedules = {};
  const schedules = {};
  Object.entries(tasksByKey).forEach(([key, task]) => {
    const normalized = normalizeDaySchedule(task?.schedule);
    if (normalized) {
      baselineSchedules[key] = normalized;
      schedules[key] = normalized;
    }
  });
  schedules[rootKey] = rootSchedule;

  // Always include the edited task write.
  writes.push({ taskKey: rootKey, schedule: rootSchedule });

  if (mode === 'pin-successors') {
    return {
      writes,
      skipped,
      affectedCount: 0,
    };
  }

  const { linksBySuccessor, successorsByPredecessor } = buildGraph(tasksByKey);
  const ordered = orderSuccessors(rootKey, successorsByPredecessor);

  for (const taskKey of ordered) {
    if (taskKey === rootKey) continue;
    const task = tasksByKey[taskKey];
    if (!task) continue;

    if (isTerminalTaskStatus(task.status)) {
      skipped.push({ taskKey, reason: 'terminal-status' });
      continue;
    }
    if (task.writable === false) {
      skipped.push({ taskKey, reason: 'no-access' });
      continue;
    }

    const current = schedules[taskKey] || normalizeDaySchedule(task.schedule);
    if (!current) {
      skipped.push({ taskKey, reason: 'missing-schedule' });
      continue;
    }

    const links = linksBySuccessor[taskKey] || [];
    const baselineSuccessor = baselineSchedules[taskKey] || current;
    let earliest = null;
    links.forEach((link) => {
      const predSchedule = schedules[link.taskKey];
      if (!predSchedule) return;
      const lagDays = resolveCascadeLagDays(
        link,
        baselineSchedules[link.taskKey],
        baselineSuccessor,
      );
      const candidate = earliestStartFromLink(link.kind, predSchedule, lagDays);
      if (!candidate) return;
      if (!earliest || candidate.getTime() > earliest.getTime()) {
        earliest = candidate;
      }
    });

    if (!earliest) continue;
    if (current.start.getTime() >= earliest.getTime()) continue;

    const next = shiftSchedulePreservingDuration(current, earliest);
    schedules[taskKey] = next;

    if (writes.length >= maxWrites) {
      skipped.push({ taskKey, reason: 'cap' });
      continue;
    }

    writes.push({ taskKey, schedule: next });
  }

  return {
    writes,
    skipped,
    affectedCount: Math.max(0, writes.length - 1),
  };
}
